/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  EntityScore,
  EntityScorePillar,
  PillarUnmeasuredReason,
  ScoreSuccessEnum,
} from '../../api/types';
import { percentToScoreSuccess } from '../../helpers/percentToScoreSuccess';

/**
 * A pillar of an aggregate (feature 0058), carrying the denominator the
 * aggregate was computed over.
 *
 * `EntityScorePillar`'s own `coverage` counts **checks** inside one System and
 * is deliberately NOT rolled up here: summing a subset's checks would present
 * that subset's denominator as the whole group's — the same misreading the
 * hybrid Security pillar refuses (feature 0037). What a group's pillar is
 * averaged over is Systems, and that is what {@link memberCoverage} states.
 */
export interface AggregatedPillar extends EntityScorePillar {
  memberCoverage: {
    /** Members that measured this pillar — the mean's denominator. */
    measured: number;
    /** Members in the group. */
    total: number;
  };
}

/** The aggregate of a group of Systems — a subdomain row's numbers. */
export interface AggregatedScore {
  /**
   * Unweighted mean of the members' Totals, rounded; `null` when no member is
   * scored. Never 0: "not scored" and "scored zero" are different statements
   * (feature 0017), and the board renders them differently.
   */
  scorePercent: number | null;
  scoreSuccess: ScoreSuccessEnum | null;
  pillars: AggregatedPillar[];
  /** Members in the group — the `N systems` chip on the row. */
  memberCount: number;
  /** Members with a Total — the denominator {@link scorePercent} used. */
  scoredCount: number;
}

const mean = (values: number[]): number =>
  Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);

/**
 * The one value shared by every element, or `undefined` when they disagree or
 * when any element has none. Unanimity is the bar for carrying a qualifier up
 * to the group: a chip that says "code only" must be true of the whole mean,
 * not of two thirds of it.
 */
function unanimous<T>(values: (T | undefined)[]): T | undefined {
  if (values.length === 0 || values.some(value => value === undefined)) {
    return undefined;
  }
  const [first] = values;
  return values.every(value => value === first) ? first : undefined;
}

/**
 * Collapses a group of Systems into one set of numbers: the **unweighted mean**
 * of the members that have a value, `null`s excluded from the denominator
 * (feature 0058).
 *
 * It is the formula the portfolio trend (`buildPortfolioTrend`) and the KPI
 * strip (`summariseScores`) already use, deliberately — a second formula would
 * put a subdomain's row and its own trend line in disagreement on one screen.
 *
 * Three rules it exists to hold:
 *
 * 1. `null` is "not scored", never 0. A group whose every member is unscored
 *    has a `null` Total, and a pillar no member measured stays `measured:
 *    false` with no score at all.
 * 2. Each pillar has its own denominator — the members that measured *that*
 *    pillar — carried on {@link AggregatedPillar.memberCoverage}.
 * 3. A qualifier only travels up when it is unanimous (see {@link unanimous}).
 */
export function aggregateScores(members: EntityScore[]): AggregatedScore {
  const totals = members
    .map(member => member.scorePercent)
    .filter((value): value is number => typeof value === 'number');
  const scorePercent = totals.length === 0 ? null : mean(totals);

  // Pillar keys in first-seen order: IMC's order is the canonical one, and the
  // table picks the columns it wants by key anyway.
  const keys: string[] = [];
  for (const member of members) {
    for (const pillar of member.pillars ?? []) {
      if (pillar.key && !keys.includes(pillar.key)) {
        keys.push(pillar.key);
      }
    }
  }

  const pillars = keys.map(key => {
    const declared = members
      .map(member => member.pillars?.find(pillar => pillar.key === key))
      .filter((pillar): pillar is EntityScorePillar => pillar !== undefined);
    const measured = declared.filter(
      pillar => pillar.measured && typeof pillar.scorePercent === 'number',
    );
    const template = declared[0];
    const memberCoverage = {
      measured: measured.length,
      total: members.length,
    };

    if (measured.length === 0) {
      // Nothing measured it: a placeholder, exactly as an unmeasured pillar is
      // on a System — no score key at all, so `measuredScoreOf` reads it as
      // unmeasured rather than as a 0.
      const unmeasuredReason = unanimous<PillarUnmeasuredReason>(
        declared.map(pillar => pillar.unmeasuredReason),
      );
      const pillar: AggregatedPillar = {
        id: template.id,
        key,
        title: template.title,
        measured: false,
        memberCoverage,
      };
      return unmeasuredReason ? { ...pillar, unmeasuredReason } : pillar;
    }

    const value = mean(measured.map(pillar => pillar.scorePercent as number));
    // A qualifier travels up only when every member of the group measured the
    // pillar AND every one of them carries the same one: `PartialScoreChip`
    // says "code only", which is false of a mean that also contains a System
    // scored in full.
    const partialReason =
      measured.length === members.length
        ? unanimous<PillarUnmeasuredReason>(
            measured.map(pillar => pillar.partialReason),
          )
        : undefined;
    const pillar: AggregatedPillar = {
      id: template.id,
      key,
      title: template.title,
      measured: true,
      scorePercent: value,
      scoreSuccess: percentToScoreSuccess(value),
      memberCoverage,
    };
    return partialReason ? { ...pillar, partialReason } : pillar;
  });

  return {
    scorePercent,
    // Derived from the mean, never inherited: the members' own verdicts are
    // about their own numbers, and the group's number is a new one.
    scoreSuccess:
      scorePercent === null
        ? null
        : percentToScoreSuccess(scorePercent) ?? null,
    pillars,
    memberCount: members.length,
    scoredCount: totals.length,
  };
}
